import React, {useState, useEffect} from "react";
import { getPhotos, deletePhoto } from "src/redux/api/_hr.api";
import { MapPin, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { FallingLines } from "react-loader-spinner";

interface UploadedPhotos {
  id: string,  
  state: string;
  key: string;
}

interface GroupedPhotos {
  state: string;
  photos: UploadedPhotos[];
}

interface DeletePhotosProps {
  refreshTrigger?: number; // Add optional prop to trigger refresh
}

const CDN_URL = "https://d3ir3etrl1n0sj.cloudfront.net/";

function DeletePhotos({ refreshTrigger }: DeletePhotosProps) {
  const [uploadedPhotos, setUploadedPhotos] = useState<UploadedPhotos[]>([]);
  const [grouped, setGrouped] = useState<GroupedPhotos[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState(false);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      const res: UploadedPhotos[] = await getPhotos();
      console.log(res);
      
      // Store the response in uploadedPhotos
      const reversed = [...res].reverse()
      setUploadedPhotos(reversed);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching photos:", err);
      setError("Failed to load photos");
      setLoading(false);
    }
  };

  const handleDeletePhoto = async(id: string) => {
    try {
        setStatus(true)
        await deletePhoto(id)
        toast.success("Photo deleted successfully")
        fetchPhotos();
    } catch (error) {
        console.error(error);
        toast.error("deletion failed");
        setStatus(false);
    }
  }

  useEffect(() => {
    fetchPhotos();
  }, []);

  // Add useEffect to refetch when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchPhotos();
    }
  }, [refreshTrigger]);

  useEffect(() => {
    // Group photos by state whenever uploadedPhotos changes
    const map = new Map<string, UploadedPhotos[]>();

    for (const item of uploadedPhotos) {
      if (!map.has(item.state)) {
        map.set(item.state, []);
      }
      map.get(item.state)!.push(item);
    }

    // Convert map to array
    const result: GroupedPhotos[] = [];
    map.forEach((photos, state) => {
      result.push({ state, photos });
    });

    setGrouped(result);
  }, [uploadedPhotos]);


  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center text-gray-600">Loading photos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center text-red-600">{error}</div>
      </div>
    );
  }

  if (grouped.length === 0) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Uploaded Photos</h1>
        <div className="text-center text-gray-600">No photos found</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Uploaded Photos</h1>

      {grouped.map((group, idx) => (
        <div key={idx} className="mb-12">
          {/* State Header */}
          <div className="flex items-center gap-2 mb-6 pb-2 border-b-2 border-gray-300">
            <MapPin size={24} className="text-blue-600" />
            <h2 className="text-2xl font-semibold text-gray-700">{group.state}</h2>
            <span className="ml-auto text-sm text-gray-500">
              {group.photos.length} {group.photos.length === 1 ? "photo" : "photos"}
            </span>
          </div>

          {/* Photos Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {group.photos.map((photo, i) => (
              <div
                key={i}
                className="relative group overflow-hidden rounded-xl border border-gray-200 shadow hover:shadow-xl transition-all duration-300"
              >
                <img
                  src={CDN_URL + photo.key}
                  alt={`${group.state}-${i}`}
                  className="w-full h-64 object-cover"
                />

                {/* Overlay with delete button */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                  <button
                    onClick={() => handleDeletePhoto(photo.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-lg transform hover:scale-110"
                    aria-label="Delete photo"
                  >
                    {status ? <FallingLines width="25" color="white"/> :
                    <Trash2 size={20}/> }
                  </button>
                </div>

                {/* Photo filename */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                  <p className="text-white text-xs truncate">{photo.key}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default DeletePhotos;